(() => {
  'use strict';
  const fail = code => { throw new Error(code); };
  const integer = (value, min = 0) => Number.isSafeInteger(value) && value >= min;
  function lines(cart) {
    if (!Array.isArray(cart?.items) || cart.items.length > 100) fail('SHOPIFY_CART_INVALID');
    const seen = new Set();
    return cart.items.map(item => {
      const id = String(item.variant_id);
      if (!/^[1-9]\d*$/.test(id) || seen.has(id) || !integer(item.quantity, 1) || item.quantity > 100000 ||
        item.selling_plan_allocation || item.gift_card || Object.keys(item.properties || {}).length) fail('SHOPIFY_CART_REQUIRES_REVIEW');
      seen.add(id);
      return { id, quantity: item.quantity };
    });
  }
  function verify(cart, quote, taxesIncluded) {
    const actual = lines(cart);
    if (cart.currency !== quote.currency || (Object.hasOwn(cart, 'taxes_included') && cart.taxes_included !== taxesIncluded)) fail('SHOPIFY_CART_CONTEXT_CHANGED');
    if (actual.length !== quote.lines.length || actual.some(item => !quote.lines.some(line => line.id === item.id && line.quantity === item.quantity))) fail('SHOPIFY_CART_QUANTITIES_CHANGED');
    for (const item of cart.items) {
      const expected = quote.lines.find(line => line.id === String(item.variant_id));
      if (!integer(item.original_price, 1) || item.original_price !== expected.baseUnitPriceCents ||
        item.original_line_price !== expected.baseTotalCents || item.final_line_price !== expected.totalCents) fail('SHOPIFY_CART_PRICES_CHANGED');
    }
    if (cart.original_total_price !== quote.baseSubtotalCents || cart.total_price !== quote.totalCents || cart.total_discount !== quote.discountCents) fail('SHOPIFY_CART_TOTAL_CHANGED');
    return cart;
  }
  function createClient({ store, origin, routesRoot = '/', currency, taxesIncluded, pricingClient, eligibility, fetch: request = globalThis.fetch, pricing = globalThis.TalsemNativePricing }) {
    if (store !== 'dachshundvillage.myshopify.com') fail('WRONG_STORE');
    const base = new URL(routesRoot, origin);
    if (base.protocol !== 'https:' || base.origin !== new URL(origin).origin || !['talsem.com', 'www.talsem.com', store].includes(base.hostname) || !base.pathname.endsWith('/') || base.search || base.hash) fail('INVALID_SHOPIFY_ROUTE');
    if (!['USD', 'CAD', 'EUR', 'GBP'].includes(currency) || typeof taxesIncluded !== 'boolean' || typeof pricingClient?.quote !== 'function' || !pricing) fail('INVALID_SHOPIFY_CONTEXT');
    pricing.validateEligibility(eligibility);
    let pending = false, uncertain = false;
    async function api(path, body) {
      const response = await request(new URL(path, base).href, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
        headers: body ? { 'Content-Type': 'application/json' } : {}, body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) fail('SHOPIFY_CART_REQUEST_FAILED');
      return response.json();
    }
    async function get() {
      const cart = await api('cart.js');
      // Ajax carts can omit taxes_included; the Liquid configuration remains authoritative.
      if (!Array.isArray(cart.items) || cart.currency !== currency || (Object.hasOwn(cart, 'taxes_included') && cart.taxes_included !== taxesIncluded)) fail('SHOPIFY_CART_CONTEXT_CHANGED');
      return cart;
    }
    async function exclusive(action) {
      if (pending) fail('SHOPIFY_CART_BUSY');
      if (uncertain) fail('SHOPIFY_CART_RELOAD_REQUIRED');
      pending = true;
      try { return await action(); }
      finally { pending = false; }
    }
    async function writeAndRead(path, body, expected) {
      let error;
      try { await api(path, body); }
      catch (failure) { error = failure; }
      let cart;
      try { cart = await get(); }
      catch { uncertain = true; fail('SHOPIFY_CART_RELOAD_REQUIRED'); }
      let matches;
      try {
        matches = typeof expected === 'function' ? expected(cart) : (() => {
          const current = lines(cart);
          return current.length === expected.length && current.every(line => expected.some(item => item.id === line.id && item.quantity === line.quantity));
        })();
      } catch (error) { uncertain = true; throw error; }
      if (!matches) {
        // Keep the server's basket intact. A reload lets the customer review an
        // inventory adjustment or concurrent edit without adding twice.
        uncertain = true;
        fail(error ? 'SHOPIFY_CART_WRITE_UNCERTAIN' : 'SHOPIFY_CART_QUANTITIES_CHANGED');
      }
      return cart;
    }
    async function add(id, quantity) {
      return exclusive(async () => {
        id = String(id);
        pricing.normalizeBasket([{ id, quantity }], eligibility);
        const before = await get();
        const expected = lines(before);
        const existing = expected.find(line => line.id === id);
        if (existing) existing.quantity += quantity;
        else expected.push({ id, quantity });
        if (expected.length > 100 || expected.some(line => line.quantity > 100000)) fail('SHOPIFY_CART_QUANTITY_LIMIT');
        return writeAndRead('cart/add.js', { items: [{ id, quantity }] }, expected);
      });
    }
    async function set(key, quantity) {
      return exclusive(async () => {
        if (!integer(quantity) || quantity > 100000) fail('SHOPIFY_CART_QUANTITY_LIMIT');
        const before = await get();
        const selected = before.items.find(item => item.key === key);
        if (!selected) fail('SHOPIFY_CART_LINE_CHANGED');
        if (!quantity) {
          const signature = items => JSON.stringify(items.map(item => JSON.stringify({
            id: String(item.variant_id), quantity: item.quantity, giftCard: Boolean(item.gift_card),
            plan: item.selling_plan_allocation?.selling_plan?.id || null,
            properties: Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
          })).sort());
          const expected = signature(before.items.filter(item => item.key !== key));
          return writeAndRead('cart/change.js', { id: key, quantity: 0 }, cart => signature(cart.items) === expected);
        }
        if (quantity) pricing.normalizeBasket([{ id: String(selected.variant_id), quantity }], eligibility);
        const expected = lines(before).map(line => line.id === String(selected.variant_id) ? { ...line, quantity } : line).filter(line => line.quantity);
        return writeAndRead('cart/change.js', { id: key, quantity }, expected);
      });
    }
    async function prepare({ enabled }) {
      if (enabled !== true) fail('CHECKOUT_DISABLED');
      return exclusive(async () => {
        const cart = await get();
        const basket = pricing.normalizeBasket(lines(cart), eligibility);
        if (!basket.length) fail('SHOPIFY_CART_EMPTY');
        const quote = await pricingClient.quote(basket, { fresh: true });
        verify(cart, quote, taxesIncluded);
        await api('cart/update.js', { attributes: { talsem_request_type: 'shipping_quote' } });
        const refreshed = await get();
        verify(refreshed, quote, taxesIncluded);
        if (refreshed.attributes?.talsem_request_type !== 'shipping_quote') fail('SHOPIFY_CART_REQUEST_TYPE_MISSING');
        return { checkoutUrl: new URL('checkout', base).href, quote };
      });
    }
    return Object.freeze({ get, add, set, prepare });
  }
  const api = Object.freeze({ lines, verify, createClient });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.TalsemShopifyCart = api;
})();
